import { useState } from 'react'
import { FormDelegate, WellMode } from '../../../../../../../types/routing-types'
import ConfirmTransition from '../../../../../../components/confirm-transition'
import { useGetWellData, useSaveWellData } from '../../../../../../hooks/use-api'
import useEditForm from '../../../../../../hooks/use-edit-form'
import WellEditButtons from '../../../../../../components/wells/well-edit-buttons'
import { AquiferTestFieldsEdit } from '../../../../../../../types/form-types'
import AquiferTestEditForm from '../../../../../../components/well-forms/aquifer-test/aquifer-test-edit-form'
import NotesWrapperEdit from '../../../../../../components/well-forms/notes/notes-wrapper-edit'
import useToastMessage from '../../../../../../hooks/use-toast-message'
import { useWellState } from '../../../../../../context/well-context'
import { HttpVerb } from '../../../../../../../types/api-types'
import { ERROR_MESSAGES } from '../../../../../../components/errors/messages'
import { useParamsOr404 } from '../../../../../../hooks/use-params-or-404'
import { useNavigate } from 'react-router-dom'
import { RecordContent, RecordFooter, useTitleChange } from '@msaf/core-react'

export function AquiferTestEdit({ setIsDirty }: FormDelegate) {
  const { wellState: isDraft } = useWellState()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Aquifer Test`)

  const { id: wellId, aquiferTestId } = useParamsOr404('id', 'aquiferTestId')
  const isNewAquiferTest = aquiferTestId === WellMode.NEW
  const url = `wells/${wellId}/aquifer-test/${aquiferTestId}/edit`
  const saveUrl = isNewAquiferTest
    ? `wells/${wellId}/aquifer-test/`
    : `wells/${wellId}/aquifer-test/${aquiferTestId}/edit`
  const saveAndReturnUrl = `/wells/${wellId}/edit/aquifer-tests`
  const backUrl = isNewAquiferTest
    ? `/wells/${wellId}/view/aquifer-tests`
    : `/wells/${wellId}/view/aquifer-tests/${aquiferTestId}`
  const requestMethod = isNewAquiferTest ? HttpVerb.POST : HttpVerb.PUT
  const navigate = useNavigate()
  const stateQueryResult = useGetWellData(url)
  const saveMutationResult = useSaveWellData(saveUrl, requestMethod)

  const {
    fields,
    isLoading,
    isSaveError,
    error,
    isSaved,
    setValue,
    handleSave,
    addField,
    removeField,
    editField,
    validationErrors,
  } = useEditForm<AquiferTestFieldsEdit>({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  return (
    <>
      <RecordContent>
        <AquiferTestEditForm
          fields={fields}
          isLoading={isLoading}
          wellId={wellId}
          setValue={setValue}
          removeField={removeField}
          addField={addField}
          validationErrors={validationErrors}
        />
      </RecordContent>

      <RecordFooter
        areActionsFixed
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
      >
        <WellEditButtons
          actions={[
            {
              label: 'Save and return',
              buttonStyle: 'primary',
              onClick: () =>
                handleSave(async (id, refetch) => {
                  await refetch?.()
                  navigate(saveAndReturnUrl, {
                    state: {
                      isProgrammaticTransition: true,
                    },
                  })
                }),
            },
            {
              label: 'Save',
              buttonStyle: 'primary',
              onClick: () =>
                handleSave(async (id, refetch) => {
                  await refetch?.()
                  isNewAquiferTest &&
                    navigate(`/wells/${wellId}/edit/aquifer-tests/${id}`, { state: { isProgrammaticTransition: true } })
                }),
            },
            {
              label: 'Return to view',
              buttonStyle: 'text-action',
              onClick: () => navigate(backUrl),
            },
          ]}
        />
      </RecordFooter>
    </>
  )
}

export default function EditAquiferTestWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <AquiferTestEdit setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
